"use client"

import { useState, useEffect } from "react"
import { SettingsSection } from "./settings-section"
import { SettingsItem } from "./settings-item"
import { Button } from "../button"
import { User, GraduationCap } from "lucide-react"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { COUNTRIES, EXAM_BODIES, CLASS_LEVELS } from "@/data/educationSystems"

function SelectField({
  id, label, value, options, onChange, placeholder,
}: {
  id: string
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  placeholder: string
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

export function AccountPreferences() {
  const [country, setCountry]     = useState('')
  const [examBody, setExamBody]   = useState('')
  const [classLevel, setClassLevel] = useState('')
  const [saved, setSaved]         = useState(false)

  // Load current values from student preferences
  useEffect(() => {
    const user = AuthStore.getState().currentUser
    if (!user) return
    const students: any[] = StorageService.read('students')
    const student = students.find((s) => s.userId === user._id || s.email === user.email)
    if (student?.preferences) {
      setCountry(student.preferences.country ?? '')
      setExamBody(student.preferences.examBody ?? '')
      setClassLevel(student.preferences.classLevel ?? '')
    }
  }, [])

  // Reset dependent fields when country changes
  const handleCountryChange = (v: string) => {
    setCountry(v)
    setExamBody('')
    setClassLevel('')
  }

  const handleExamBodyChange = (v: string) => {
    setExamBody(v)
    setClassLevel('')
  }

  const handleSave = async () => {
    const user = AuthStore.getState().currentUser
    if (!user) return
    const students: any[] = StorageService.read('students')
    const idx = students.findIndex((s) => s.userId === user._id || s.email === user.email)
    if (idx === -1) return
    students[idx] = {
      ...students[idx],
      preferences: {
        ...students[idx].preferences,
        country,
        examBody,
        classLevel,
      },
      updatedAt: new Date(),
    }
    StorageService.write('students', students)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const examBodies = country ? (EXAM_BODIES[country] ?? []).map((e) => ({ value: e.code, label: e.name })) : []
  const classLevels = examBody ? (CLASS_LEVELS[examBody] ?? []).map((l) => ({ value: l, label: l })) : []

  return (
    <>
      <SettingsSection title="Account Preferences" icon={User}>
        <SettingsItem
          title="Update Email"
          description="Change the email associated with your account."
          action={<Button className="bg-indigo-600 hover:bg-indigo-700">Update</Button>}
        />
        <SettingsItem
          title="Change Password"
          description="Keep your account secure with a new password."
          action={<Button className="bg-indigo-600 hover:bg-indigo-700">Change</Button>}
        />
      </SettingsSection>

      <SettingsSection title="Education System" icon={GraduationCap}>
        <p className="text-sm text-slate-500 -mt-2">
          Set your country and exam system so your curriculum and career paths match your syllabus.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-lg bg-slate-50">
          <SelectField
            id="country"
            label="Country"
            value={country}
            options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
            onChange={handleCountryChange}
            placeholder="Select country"
          />
          <SelectField
            id="exam-body"
            label="Exam Body / System"
            value={examBody}
            options={examBodies}
            onChange={handleExamBodyChange}
            placeholder={country ? 'Select system' : 'Choose country first'}
          />
          <SelectField
            id="class-level"
            label="Current Class / Level"
            value={classLevel}
            options={classLevels}
            onChange={setClassLevel}
            placeholder={examBody ? 'Select level' : 'Choose system first'}
          />
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!country || !examBody || !classLevel}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40"
          >
            {saved ? 'Saved' : 'Save Education Settings'}
          </Button>
        </div>
      </SettingsSection>
    </>
  )
}
